import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { DataView } from "primereact/dataview";
import { Panel } from "primereact/panel";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ToastContext } from "src/core/context/ToastContext";
import { useErrorHandler } from "src/core/error/useErrorHandler";
import { useError } from "src/core/hooks/useError";
import { logService } from "src/core/services/logService";
import { useAppointmentStore } from "src/features/appointment/services/useAppointmentStore";
import { useAuthStore } from "src/features/auth/services/useAuthStore";
import { ClinicDoctorModel } from "src/features/clinics/models/clinicDoctorModel";
import { ClinicModel } from "src/features/clinics/models/clinicModel";
import { useClinicsStore } from "src/features/clinics/services/useClinicsStore";
import { FavouriteDoctorSelect } from "src/features/favourite/components/FavouriteDoctorSelect/FavouriteDoctorSelect";
import { useFavouriteStore } from "src/features/favourite/services/useFavouriteStore";
import { ParticipantModel } from "src/features/participant/models/participantModel";
import "./HomeFavouriteDoctors.css";
import { DoctorProfileModel } from "src/features/clinics/models/doctorProfileModel";

export function HomeFavouriteDoctors() {
  const toastRef = useContext(ToastContext);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getErrorMessage } = useError();

  const authStore = useAuthStore();
  const clinicsStore = useClinicsStore();
  const favouriteStore = useFavouriteStore();
  const appointmentStore = useAppointmentStore();
  const errorHandler = useErrorHandler();

  const [favouriteClinicDoctors, setFavouriteClinicDoctors] =
    useState<ClinicDoctorModel[]>();

  const [doctorSelectVisible, setDoctorSelectVisible] = useState(false);

  useEffect(() => {
    favouriteStore.favouriteList();
  }, []);

  useEffect(() => {
    const clinicDoctors = clinicsStore.clinicDoctors;
    const favourites = favouriteStore.favourites;

    const favouriteClinicDoctors = clinicDoctors
      .filter((clinicDoctor) => {
        const favourite = favourites.find(
          (favourite) =>
            favourite.doctorId === clinicDoctor.doctorId &&
            favourite.clinicId === clinicDoctor.clinicId
        );
        clinicDoctor.favouriteId = favourite?.id ?? 0;
        return !!favourite;
      })
      .sort((a, b) => {
        return a.doctorName.localeCompare(b.doctorName);
      });

    if (logService.globalLog)
      logService.log("favouriteClinicDoctors", favouriteClinicDoctors);

    setFavouriteClinicDoctors(favouriteClinicDoctors);
  }, [clinicsStore.clinicDoctors, favouriteStore.favourites]);

  const onFavouriteDoctorDelete = (
    event: React.MouseEvent<HTMLButtonElement>,
    clinicDoctor: ClinicDoctorModel
  ) => {
    logService.log("onFavouriteDelete", clinicDoctor);

    if (clinicDoctor.favouriteId === 0) return;

    confirmPopup({
      target: event.target as HTMLElement,
      message: t("Home.FavouriteDoctors.DeleteSure"),
      icon: "pi pi-question-circle",
      defaultFocus: "accept",
      accept: async () => {
        clinicDoctor.isLoading = true;

        await favouriteStore
          .favouriteDelete(clinicDoctor.favouriteId)
          .then(() => {
            toastRef?.current?.show({
              severity: "success",
              summary: t("Home.FavouriteDoctors.Title"),
              detail: t("Home.FavouriteDoctors.DeleteSuccess"),
              life: 5000,
            });

            favouriteStore.favouriteList();
          })
          .catch((error) => {
            errorHandler.setError({
              title: t("Home.FavouriteDoctors.Title"),
              message:
                t("Home.FavouriteDoctors.DeleteFailed") +
                " " +
                getErrorMessage(error),
            });
          });

        clinicDoctor.isLoading = false;
      },
    });
  };

  const onMakeAnAppointment = (clinicDoctor: ClinicDoctorModel) => {
    if (logService.globalLog)
      logService.log("onMakeAnAppointment", clinicDoctor);

    appointmentStore.setSelectedPerson(
      new ParticipantModel({
        id: 0,
        userId: 0,
        name: t("Home.Appointments.ForMyself"),
        phone: authStore.user?.phone,
      })
    );

    appointmentStore.setSelectedClinic(
      new ClinicModel({
        id: clinicDoctor.clinicId,
        name: clinicDoctor.clinicName,
      })
    );

    appointmentStore.setSelectedDoctorProfile(
      new DoctorProfileModel({
        doctorId: clinicDoctor.doctorId,
        doctorName: clinicDoctor.doctorName,
        profileId: 0,
        profileName: "",
      })
    );

    appointmentStore.setQuickAppointment(true);

    navigate("/appointment");
  };

  return (
    <div className="homeFavouriteDoctors">
      <Panel
        header={
          <div className="flex align-items-center gap-2">
            <span className="pi pi-briefcase"></span>
            <span className="font-bold">
              {t("Home.FavouriteDoctors.Title")}
            </span>
          </div>
        }
        icons={
          <Button
            icon="pi pi-file-plus"
            label={t("Home.FavouriteDoctors.New")}
            text
            onClick={() => {
              setDoctorSelectVisible(true);
            }}
          ></Button>
        }
      >
        <DataView
          value={favouriteClinicDoctors}
          listTemplate={(items?: ClinicDoctorModel[]) => {
            if (!items || items.length === 0) return null;

            let list = items.map((item, index) => {
              return (
                <div
                  key={item.favouriteId}
                  className={
                    "col-12 " + (index > 0 ? "border-top-1 surface-border" : "")
                  }
                >
                  <div className="flex flex-row align-items-center gap-3">
                    <div className="flex flex-column flex-grow-1">
                      <div className="flex flex-row gap-1">
                        <span className="pi pi-star-fill"></span>
                        <div className="font-bold">{item.doctorName}</div>
                      </div>
                      <div>{item.clinicName}</div>
                      <div className="font-italic">{item.clinicAddress}</div>
                      {item.doctorImagePath && (
                        <img
                          src={item.doctorImagePath}
                          alt={item.doctorName}
                          style={{ maxWidth: ": 150px;" }}
                        />
                      )}
                      <div>
                        <Button
                          icon="pi pi-calendar-plus"
                          label={t("Home.FavouriteDoctors.MakeAnAppointment")}
                          severity="contrast"
                          onClick={() => onMakeAnAppointment(item)}
                        ></Button>
                      </div>
                    </div>
                    <div>
                      <Button
                        icon="pi pi-trash"
                        severity="danger"
                        onClick={(event) =>
                          onFavouriteDoctorDelete(event, item)
                        }
                      ></Button>
                    </div>
                  </div>
                </div>
              );
            });

            return <>{list}</>;
          }}
        />
      </Panel>

      <FavouriteDoctorSelect
        visible={doctorSelectVisible}
        onHide={() => setDoctorSelectVisible(false)}
      />
    </div>
  );
}
